import { Link } from 'react-router-dom';
import { Layers } from 'lucide-react';
import { colors } from '../lib/theme';
import { LENS_LABELS } from '../lib/lens-labels';
import type { PortfolioLens, PortfolioSummary } from '../services/api';

const LENS_DISPLAY: Record<PortfolioLens, string> = {
  bds: LENS_LABELS.occupation,
  defense: LENS_LABELS.defense,
  surveillance: 'Surveillance',
  shariah: LENS_LABELS.shariah,
};

const LENS_SHORT: Record<PortfolioLens, string> = {
  bds: LENS_LABELS.occupationShort,
  defense: LENS_LABELS.defenseShort,
  surveillance: 'Surveillance',
  shariah: LENS_LABELS.shariahShort,
};

function scoreColor(score: number): string {
  if (score >= 80) return colors.green;
  if (score >= 50) return colors.yellow;
  return colors.red;
}

function ScoreRing({ score, size = 120, stroke = 10 }: { score: number; size?: number; stroke?: number }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const off = circ * (1 - score / 100);
  const col = scoreColor(score);
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={colors.bgRaised} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={col}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={off}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center leading-none">
        <div>
          <div className="font-mono-num text-[36px] font-bold tabular-nums" style={{ color: col }}>
            {score}
          </div>
          <div className="font-mono text-[11px] mt-1" style={{ color: colors.textMuted }}>
            / 100
          </div>
        </div>
      </div>
    </div>
  );
}

export function ComplianceScoreHero({
  portfolio,
}: {
  portfolio: PortfolioSummary;
}) {
  const lenses = Object.keys(portfolio.lensScores) as PortfolioLens[];

  return (
    <div
      className="mt-10 rounded-2xl p-7"
      style={{
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bgSecondary,
      }}
    >
      <div className="flex flex-wrap gap-8 items-center">
        <div className="flex items-center gap-6 flex-1 min-w-[280px]">
          <ScoreRing score={portfolio.overallScore} />
          <div>
            <div className="font-mono text-[11.5px] uppercase tracking-wide" style={{ color: colors.textMuted }}>
              Portfolio compliance score
            </div>
            <p className="mt-2.5 text-sm leading-relaxed max-w-xs" style={{ color: colors.textSecondary }}>
              {portfolio.weighting === 'dollar'
                ? portfolio.totalValueUsd != null
                  ? `Dollar-weighted across $${portfolio.totalValueUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })} in holdings.`
                  : 'Weighted by dollar amounts in your CSV.'
                : 'Estimated using equal weight per ticker (add Market Value for dollar weighting).'}
            </p>
            <Link to="/methodology" className="mt-2.5 inline-block text-[13px]" style={{ color: colors.blue }}>
              How we calculate this →
            </Link>
          </div>
        </div>
        {lenses.length > 0 && (
          <div
            className="flex-1 min-w-[280px] grid gap-2.5"
            style={{ gridTemplateColumns: `repeat(${Math.min(lenses.length, 3)}, minmax(0, 1fr))` }}
          >
            {lenses.map((lens) => {
              const score = portfolio.lensScores[lens] ?? 0;
              const exp = portfolio.exposureByLens[lens];
              return (
                <div
                  key={lens}
                  className="rounded-xl px-3 py-2.5"
                  style={{ backgroundColor: colors.bgTertiary, border: `1px solid ${colors.border}` }}
                >
                  <div className="flex justify-between items-baseline gap-2 text-xs" style={{ color: colors.textSecondary }}>
                    <span className="truncate">{LENS_SHORT[lens]}</span>
                    <span className="font-mono-num font-semibold tabular-nums" style={{ color: scoreColor(score) }}>
                      {score}
                    </span>
                  </div>
                  {exp && portfolio.weighting === 'dollar' && (
                    <div className="mt-1 font-mono text-[10px]" style={{ color: colors.textFaint }}>
                      ${exp.excludedUsd.toFixed(0)} excluded
                      {exp.reviewUsd > 0 ? ` · $${exp.reviewUsd.toFixed(0)} review` : ''}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function EtfXRayPanel({ portfolio }: { portfolio: PortfolioSummary }) {
  if (!portfolio.etfBreakdown?.length) return null;

  const byEtf = new Map<string, typeof portfolio.etfBreakdown>();
  for (const entry of portfolio.etfBreakdown) {
    const list = byEtf.get(entry.etfSymbol) ?? [];
    list.push(entry);
    byEtf.set(entry.etfSymbol, list);
  }

  return (
    <div className="mt-12 space-y-6">
      {Array.from(byEtf.entries()).map(([etfSymbol, entries]) => {
        const flaggedUsd = entries.reduce((sum, e) => sum + e.excludedUsd + e.reviewUsd, 0);
        return (
          <div
            key={etfSymbol}
            className="rounded-2xl overflow-hidden"
            style={{
              border: `1px solid ${colors.yellowDim}4d`,
              backgroundColor: colors.bgSecondary,
            }}
          >
            <div
              className="px-7 pt-6 pb-5"
              style={{
                borderBottom: `1px solid ${colors.border}`,
                backgroundColor: `${colors.yellowDim}0f`,
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <Layers className="h-4 w-4" style={{ color: colors.yellow }} />
                <span className="font-mono text-[11.5px] uppercase tracking-wider" style={{ color: colors.yellow }}>
                  ETF X-Ray · {etfSymbol}
                </span>
              </div>
              <h3 className="display text-[28px] sm:text-[34px] tracking-tight leading-tight m-0 font-normal">
                The fund you thought was neutral.
              </h3>
              <p className="mt-3 text-[15px] leading-relaxed max-w-xl" style={{ color: colors.textSecondary }}>
                <span className="font-mono" style={{ color: colors.textPrimary }}>
                  {etfSymbol}
                </span>{' '}
                is a basket, not a company. Look through it and the flagged names inside this position show up in dollars.
              </p>
            </div>
            <div className="grid md:grid-cols-[minmax(200px,0.8fr)_1.4fr]">
              <div className="px-7 py-6 flex flex-col justify-center md:border-r" style={{ borderColor: colors.border }}>
                <div className="font-mono text-[11.5px] uppercase tracking-wide" style={{ color: colors.textMuted }}>
                  Inside this position
                </div>
                <div className="font-mono-num text-[44px] font-bold leading-none mt-2.5" style={{ color: colors.red }}>
                  ${Math.round(flaggedUsd).toLocaleString()}
                </div>
                <div className="font-mono text-[12.5px] mt-1.5" style={{ color: colors.textMuted }}>
                  flagged across {entries.length} lens{entries.length === 1 ? '' : 'es'}
                </div>
              </div>
              <div className="px-7 py-5">
                {entries.map((e, i) => (
                  <div
                    key={`${etfSymbol}-${e.lens}`}
                    className="py-3.5"
                    style={{
                      borderBottom: i < entries.length - 1 ? `1px solid ${colors.borderDark}` : 'none',
                    }}
                  >
                    <div className="flex items-center justify-between gap-3 mb-2">
                      <span className="text-[13.5px] font-semibold">{LENS_DISPLAY[e.lens]}</span>
                      <span className="font-mono-num text-sm font-bold" style={{ color: colors.red }}>
                        ${e.excludedUsd.toFixed(0)}
                        {e.reviewUsd > 0 ? (
                          <span style={{ color: colors.yellow }}> + ${e.reviewUsd.toFixed(0)}</span>
                        ) : null}
                      </span>
                    </div>
                    {e.topHoldings.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {e.topHoldings.slice(0, 4).map((h) => (
                          <span
                            key={h.symbol}
                            className="font-mono text-[11px] rounded-md px-2 py-0.5"
                            style={{
                              color: colors.textSecondary,
                              backgroundColor: colors.bgTertiary,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            {h.symbol}{' '}
                            <span style={{ color: colors.textFaint }}>{h.weightPct.toFixed(2)}%</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
